import type { ConstructionS3Info  } from "@/types/constructionApiDef"
import type { KokubanInfo  } from "@/types/kokubanApiDef"
import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { defineStore } from "pinia"
import { ref } from 'vue'
import { useAppErrorStore } from "@/stores/appError"
import { checkClientTimeSkewed, ClientTimeTooSkewedError } from "@/utils/s3_time_check"

const REGION = 'ap-northeast-1'

// 発行 URL の 有効期間
const DEFAULT_URL_EXPIRES = 10800 // 3 hours

class UrlBuilder {
  _bucket: string
  _s3: S3Client

  constructor(bucket: string, s3Client: S3Client){
    this._bucket = bucket;
    this._s3 = s3Client
  }

  static async build(s3Info: ConstructionS3Info) {
    const bucket = s3Info.s3.bucket;
    const s3Client = new S3Client({
      ...('endpoint' in s3Info.s3
        ? {
          endpoint: s3Info.s3.endpoint,
          forcePathStyle: true,
        }
        : {}
      ),
      ...('region' in s3Info.s3
        ? {
          region: s3Info.s3.region
        }
        : {
          region: REGION
        }
      ),
      credentials: {
        accessKeyId: s3Info.credentials.access_key_id,
        secretAccessKey: s3Info.credentials.secret_key,
        sessionToken: s3Info.credentials.session_token
      },
      apiVersion: "2006-03-01",
    })
    // 時刻チェック
    await checkClientTimeSkewed(s3Client, bucket, s3Info.s3.key)
    return new UrlBuilder(bucket, s3Client)
  }

  async buildUrl(key: string, downloadFileName?: string) {
    const getObjectParams: {
      Bucket: string
      Key: string
      ResponseContentDisposition?: string
    } = {
      Bucket: this._bucket,
      Key: key,
    };
    // ダウンロード用の場合
    if (downloadFileName) {
      const fn = encodeURIComponent(downloadFileName);
      getObjectParams.ResponseContentDisposition = `attachment; filename="${fn}"; filename*=UTF-8''"${fn}"`
    }
    const command = new GetObjectCommand(getObjectParams);    
    const url = await getSignedUrl(this._s3, command, { expiresIn: DEFAULT_URL_EXPIRES })
    return url
  }
}


export const useAwsStore = defineStore('aws', () => {
  
  // 工事毎でS3インスタンス
  type S3Construction = {
    urlBuilder: UrlBuilder
    bucket: string
    key: string
  }
  const s3Constructions = ref<{[key: string]: S3Construction}>({})

  const updateS3Construction = async(args: { cloud_construction_id: string, s3_info: ConstructionS3Info }) => {
    const appErrorStore = useAppErrorStore()
    try {
      // S3サービスの作成
      const urlBuilder = await UrlBuilder.build(args.s3_info);

      s3Constructions.value[args.cloud_construction_id] = {
        urlBuilder: urlBuilder,
        bucket: args.s3_info.s3.bucket,
        key: args.s3_info.s3.key,
      }
      s3Constructions.value = Object.assign({}, s3Constructions.value, s3Constructions.value)
    } catch (e: any) {
      if(e instanceof ClientTimeTooSkewedError){
        const appError = {
          error: e,
          description: `パソコンの時刻がずれていると、蔵衛門クラウドを正しくご利用いただけません
          設定をご確認のうえ正しい時刻に変更してください`
        }
        appErrorStore.updateAppError(appError)
      }
      throw e
    }
  }

  /**
  * 写真URLを作成する
  * @param {{
  *   state: object, dispatch: function, commit: function
  * }} context 
  * @param {{
  * cloud_construction_id: string,
  * s3Key: string,
  * downloadFileName? :string
  * }} args
  * @returns {{
  *    url: string,
  * }}  表示用の写真URL
  */
  type BuildPhotoUrl = {
    cloud_construction_id: string
    s3Key: string
    downloadFileName? :string
  }
  const buildPhotoUrl = async(args: BuildPhotoUrl) => {
    if(!args.cloud_construction_id) return

    const s3Construction = s3Constructions.value[args.cloud_construction_id]

    const key = `${ s3Construction.key }${ args.s3Key }`
    return await s3Construction.urlBuilder.buildUrl(key, args.downloadFileName)
  }

  /**
  * 黒板のURLを生成する.
  * @param {{
  *   state: object, dispatch: function, commit: function
  * }} context 
  * @param {{
  * cloud_construction_id: string,
  * kokuban: {
  *   s3path: string,
  * },
  * }} args
  * @returns {{
  *    url: string,
  * }} 黒板URL
  */
  type BuildKokubanUrl = {
    cloud_construction_id: string
    kokuban: KokubanInfo
  }
  const buildKokubanUrl = async(args: BuildKokubanUrl) =>{
    if(!args.cloud_construction_id) return

    const s3Construction = s3Constructions.value[args.cloud_construction_id]
    const key = `${ s3Construction.key }${ args.kokuban.s3path }`;
    return await s3Construction.urlBuilder.buildUrl(key)
  }

  /**
  * 図面のURLを生成する.
  * @param {{
  *   state: object, dispatch: function, commit: function
  * }} context 
  * @param {{
  * cloud_construction_id: string,
  * zumenEntity: {
  *   s3key: string,
  * }},
  * }} args
  * @returns {{
  *    url: string,
  *    width: number,
  *    height: number,
  * }} thumbnail情報
  */
  type BuildZumenUrl = {
    cloud_construction_id: string
    zumenEntity: any
    targetThumbnailProc? : any
  }
  const buildZumenUrl = async(args: BuildZumenUrl) => {
    if(!args.cloud_construction_id) return

    const s3Construction = s3Constructions.value[args.cloud_construction_id]

    const targetThumbnailProc =
    args.targetThumbnailProc || ((_: any, index: number) => (index === 0));

    const thumbnail = args.zumenEntity.thumbnails.find(targetThumbnailProc);
    const key = `${ s3Construction.key }${ args.zumenEntity.s3key }${ thumbnail.s3key }`;
    const url = await s3Construction.urlBuilder.buildUrl(key)
    return {
      url: url,
      width: thumbnail.width,
      height: thumbnail.height,
    }
  }

  return {
    updateS3Construction,
    buildKokubanUrl,
    buildPhotoUrl,
    buildZumenUrl,
  }
})